import { isStripeEnabled } from "common";
import {
    Delegator,
    FeatureRole,
    GratuitySource,
    PaymentMethod,
    RemoteGatewayAuthenticationType,
    RemoteGatewayObject,
    RemoteGatewayType,
    Seller,
    SellerDefaultGateway,
    SellerPaymentMethod,
    StripeAccountRemoteSystem,
    StripeAccountType,
    UserRole,
    XolaPartnerContextSeller,
} from "types";
import { getPaymentMethodName } from "./Payment";
import { queryClient } from "@/api/queryClient";
import { xolaUri } from "@/api/xolaUri";
import { isKYCNeeded } from "@/components/Settings/Payments/StripePaymentGateway";
import { getSeller } from "@/utils/getSeller";

export const getDefaultLogoUrl = (seller: Seller | Delegator | XolaPartnerContextSeller, size = "medium") => {
    // Do not add a RANDOM cache busting param here, otherwise every call will bypass the browser's caching and we may
    // have unnecessary API calls. These calls will be more severe on delegates with a lot of sellers
    let url = `${xolaUri}/api/users/${seller.id}/picture?size=${size}`;
    if (seller.picture?.id) {
        url += `&_cache=${seller.picture.id}`;
    }

    return url;
};

export const hasFeature = (seller: Seller, feature: FeatureRole | UserRole) => {
    return seller.roles?.includes(feature);
};

export const hasAnyFeature = (seller: Seller, features: FeatureRole[]) => {
    return features.some((feature) => hasFeature(seller, feature));
};

export const getCustomPaymentMethod = (seller: Seller, id: string): SellerPaymentMethod | null => {
    return seller.paymentMethods?.find((paymentMethod) => paymentMethod.id === id) ?? null;
};

export const isPurchaseItemizationEnabled = (seller: Seller): boolean => {
    return !!seller.preferences.booking?.orderItemization?.enabled;
};

export const isGratuityEnabled = (seller: Seller): boolean => {
    return !!seller.preferences.gratuity?.enabled;
};

export const isQrCodeEnabled = (seller: Seller): boolean => {
    return !!seller.preferences.qrCode?.enabled;
};

export const getDefaultPaymentMethod = (seller: Seller): SellerPaymentMethod => {
    const favouriteMethod = seller.paymentMethods?.find((method) => method.favourite);

    return (
        favouriteMethod ?? {
            id: PaymentMethod.CREDIT_CARD,
            name: getPaymentMethodName(seller, PaymentMethod.CREDIT_CARD)!,
        }
    );
};

export const isSplitPayEnabled = (seller: Seller): boolean => {
    return hasFeature(seller, FeatureRole.SPLIT_PAYMENT);
};

export const canBookPastDates = (seller: Seller): boolean => {
    return !!seller.preferences.booking?.allowPastDates;
};

export const getAddress = (seller: Seller | Delegator) => {
    let city = seller.city ?? "N/A";
    let state = seller.state ?? "N/A";

    const countryRe = new RegExp(`[^a-z](\\s|-).*${seller.country}`, "i");
    city = city.replace(countryRe, "");

    if (seller.state) {
        const stateRe = new RegExp(`[^a-z](\\s|-).*${state}`, "i");
        city = city.replace(stateRe, "").trim();
        state = state.replace(countryRe, "").trim();
    }

    return `${city}, ${state}`;
};

export const getSellerCountry = (seller: Seller | Delegator) => {
    const country = seller.country?.replace("United States", "USA") ?? "N/A";
    return country;
};

export const isAuthorizeNetEnabled = (seller: Seller) => {
    if (seller.defaultGateway?.type) {
        return [RemoteGatewayType.AUTHORIZE_NET_AIM, RemoteGatewayType.AUTHORIZE_NET_CIM].includes(
            seller.defaultGateway.type,
        );
    }

    return false;
};

export const hasCustomPaymentGateway = (seller: Seller) => {
    const { defaultGateway } = seller;
    return defaultGateway?.object === RemoteGatewayObject.CUSTOM;
};

const needsStripeAccountVerification = (seller: Seller) => {
    const { stripeAccount } = seller;

    const isStripeCustom = hasStripeCustom(seller);
    if (!stripeAccount || !isStripeCustom) {
        return false;
    }

    return isKYCNeeded(stripeAccount);
};

const isStripeUSA = (seller: Seller) => {
    const { stripeAccount } = seller;
    const isHasCustomPaymentGateway = hasCustomPaymentGateway(seller);

    return (
        !isHasCustomPaymentGateway && stripeAccount && stripeAccount.remoteSystem === StripeAccountRemoteSystem.STRIPE
    );
};

export const isStripeConnectOAuth = (defaultGateway?: SellerDefaultGateway) => {
    if (defaultGateway?.type !== RemoteGatewayType.STRIPE_OAUTH) {
        return false;
    }

    return defaultGateway?.authenticationType === RemoteGatewayAuthenticationType.OAUTH;
};

export const isShowTopUp = (seller: Seller) => {
    if (seller.currency === "USD" && !needsStripeAccountVerification(seller)) {
        return isStripeUSA(seller);
    }

    return false;
};

export const isAdditionalBillingInfoRequired = (seller: Seller) => {
    return seller.preferences.payment.requireAdditionalBillingInfo;
};

export const hasStripeCustom = (seller: Seller) => {
    return !hasCustomPaymentGateway(seller) && seller.stripeAccount?.type === StripeAccountType.CUSTOM;
};

export const isStripeXola = (seller: Seller) => {
    return seller.defaultGateway?.type ? seller.defaultGateway.type === RemoteGatewayType.STRIPE_XOLA : false;
};

export const invalidateSellerBalance = () => {
    queryClient.invalidateQueries(["sellerBalance"]);
};

export const shouldRetainPriceByDefault = () => {
    return getSeller().preferences.booking.defaultPriceStrategyForPurchaseEdit === "retain";
};

export const hasGratuityEnabledForBackOffice = () => {
    return getSeller().preferences.gratuity?.enabledSources?.includes(GratuitySource.OFFICE);
};

export const getGratuityEnabledExperiences = () => {
    return getSeller().preferences.gratuity?.experiences ?? { all: false, experiences: [] };
};

export const hasX2CheckoutWithFullProbability = () => {
    const seller = getSeller();

    const hasGateway = isStripeEnabled(seller) || isAuthorizeNetEnabled(seller);
    const hasRole = hasFeature(seller, FeatureRole.X2_CHECKOUT);
    return hasRole && hasGateway && seller?.preferences?.checkout?.x2Probability === 1;
};

export const hasX2Checkout = () => {
    const seller = getSeller();
    return hasFeature(seller, FeatureRole.X2_CHECKOUT);
};
